/**
 * Module Access Guard Component
 * Checks module access and shows restricted message if access denied
 */

import React from 'react';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import ModuleAccessRestricted from '@/components/common/ModuleAccessRestricted';
import { getModuleDisplayName } from '@/types/modules.types';

interface ModuleAccessGuardProps {
  children: React.ReactNode;
  module?: string; // Optional: explicitly specify module name
  moduleDisplayName?: string; // Optional: display name for the module
}

const ModuleAccessGuard: React.FC<ModuleAccessGuardProps> = ({
  children,
  module,
  moduleDisplayName,
}) => {
  const { hasModuleAccess } = useModuleAccess();

  // If no module specified, allow access (backward compatibility)
  if (!module) {
    return <>{children}</>;
  }

  // Check module access
  const hasAccess = hasModuleAccess(module);

  if (!hasAccess) {
    // Show restricted component with formal message
    const displayName = moduleDisplayName || getModuleDisplayName(module);
    return <ModuleAccessRestricted module={module} moduleDisplayName={displayName} />;
  }

  // User has access, render children
  return <>{children}</>;
};

export default ModuleAccessGuard;
